/**
 * `/about-us/news-events/news/<categorySlug>/<titleSlug>/`: one news article page, built for
 * every `news` entry of the current brand in Contentful.
 *
 * `getStaticPaths` takes the category and title slug pairs from
 * `src/contentful/news/getContentfulNewsSlugs.ts`. The category slug is derived from the
 * article's Contentful `category` label (`src/contentful/news/helpers/index.ts` →
 * `getNewsCategorySlugFromCategoryLabel`); a label outside its four fails the build here.
 * `getStaticProps` looks the article up by `titleSlug` alone
 * (`getContentfulNewsArticleDetails.ts`) and adds the newest-first list for the sidebar. All
 * three read the cached result of `getContentfulNewsAllFields.ts`. The page renders
 * `components/NewsEvents/News/NewsArticleContainer.tsx`.
 *
 * Unlike the other News & Events pages there is no `NEXT_PUBLIC_COMPANY` check: the Southern
 * Tool Steel build builds whatever STS news Contentful holds.
 *
 * In this repo, links here come from `components/NewsEvents/News/` (`AllNews.tsx`,
 * `LatestNewsSection.tsx`, `NewsArticle.tsx`). Pinned by
 * `src/unit-test/pages/about-us/news-events/news/categorySlug/titleSlug.spec.tsx`.
 */
import React from "react";
import { getContentfulNewsSlugs } from "@contentful/news/getContentfulNewsSlugs";
import {
	ProcessedContentfulNewsArticleAllFieldsByLanguage,
	ProcessedContentfulNewsForSectionsByLanguage,
} from "@contentful/news/types";
import { GetStaticProps } from "next";
import { ParsedUrlQuery } from "querystring";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getContentfulNewsArticleDetails } from "@contentful/news/getContentfulNewsArticleDetails";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import NewsArticleContainer from "@components/NewsEvents/News/NewsArticleContainer";
import { getContentfulNewsForSections } from "@contentful/news/getContentfulNewsForSections";

/** One path per article; any other path is a 404. */
export const getStaticPaths = async () => {
	const newsCategorySlugsAndTitleSlugs = await getContentfulNewsSlugs();

	return {
		paths: newsCategorySlugsAndTitleSlugs.map(({ categorySlug, titleSlug }) => ({
			params: {
				categorySlug,
				titleSlug,
			},
		})),
		fallback: false, // return 404 for all other paths
	};
};

interface PageParams extends ParsedUrlQuery {
	categorySlug: string;
	titleSlug: string;
}

interface PageProps {
	contentfulNewsArticleByLanguage: ProcessedContentfulNewsArticleAllFieldsByLanguage;
	contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
}

/**
 * Build-time props for one article page. `categorySlug` isn't read: the article is found by
 * `titleSlug`. Each fetcher rethrows, so a Contentful failure, or an article missing from a
 * language, fails the build.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			contentfulNewsArticleByLanguage: await getContentfulNewsArticleDetails(
				params?.titleSlug as string // type narrowing because params will be there
			),
			contentfulNewsByLanguage: await getContentfulNewsForSections(),
		},
	};
};

/** Renders the article page inside the site header and footer. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<NewsArticleContainer
				contentfulNewsArticleByLanguage={props.contentfulNewsArticleByLanguage}
				contentfulNewsByLanguage={props.contentfulNewsByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
